import { FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { AsH3, CaptionMD, CaptionSM, Text, TitleXS } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { mergeUrlParams } from '@/utils/UriBuilder/UriBuilder'
import { getAnonymousId, useSafeAnalytics } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import useHandleUnexpectedError from '@/views/TicketCheckoutViews/hooks/useHandleUnexpectedError'
import { VenueLogo } from '../../components/VenueLogo'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { PresaleVenueIntentModal } from './PresaleVenueIntentModal'

export interface PresaleVenueItemProps {
  venue: ShowtimesVenue
  theatricalRelease: TheatricalReleaseResult
}

const DISCOUNT_INTENT_PROMOS = ['vernon', 'st', 'ticket']

export const PresaleVenueItem: FC<PresaleVenueItemProps> = ({ venue, theatricalRelease }) => {
  const { isDarkMode } = useThemeContext()
  const { t } = useTranslate('theatrical-presales')
  const [isLoading, setIsLoading] = useState(false)
  const { createCart } = useShopifyCart()
  const { push } = useSafeRouter()
  const { handleUnexpectedError } = useHandleUnexpectedError({ reservationId: 'unknown' })
  const { user } = useSafeAnalytics()
  const { trackProductAdded } = useEcommerceEvents()
  const [showPresaleVenueIntentModal, setShowPresaleVenueIntentModal] = useState(false)

  const { query } = useSafeRouter()

  const logoUrl = venue.vendorLogo?.cloudinaryPath
  const movieTitle = theatricalRelease?.title

  const progressPercentage = useMemo(() => {
    if (!venue.presaleGoal) return 0
    const { ticketsSold, nextMilestone } = venue.presaleGoal
    if (!nextMilestone || nextMilestone === 0) return 0
    return Math.min((ticketsSold / nextMilestone) * 100, 100)
  }, [venue.presaleGoal])

  const shopifyGid = theatricalRelease?.unlimitedProductVariantId ?? ''

  const handleTrackProductAdded = useCallback(
    ({ price, currency, quantity }: { price: number; currency: MoneyCurrencyCode; quantity: number }) => {
      return trackProductAdded({
        category: 'theatrical pre-order ticket',
        funnel: 'theatrical-pre-sale',
        sku: `pre-order-${theatricalRelease?.theatricalSlug || 'unknown'}`,
        product_id: `pre-order-${theatricalRelease?.theatricalSlug || 'unknown'}`,
        name: `${theatricalRelease?.theatricalSlug || 'unknown'} theatrical pre-order ticket`,
        price,
        currency,
        quantity,
        projectSlug: theatricalRelease?.theatricalSlug || 'unknown',
      })
    },
    [trackProductAdded, theatricalRelease?.theatricalSlug],
  )

  const handleCheckout = useCallback(async () => {
    if (
      theatricalRelease?.theatricalSlug === slugs.youngWashington &&
      DISCOUNT_INTENT_PROMOS.includes(getItemOrFirstEntry(query?.promo) ?? '')
    ) {
      setShowPresaleVenueIntentModal(true)
      return
    }

    try {
      setIsLoading(true)

      if (!shopifyGid) {
        throw new Error('No shopify variant ID found for this theatrical release')
      }

      const attributes = [
        {
          key: 'purchase_intent_venue_id',
          value: venue.id,
        },
        { key: 'anon_id', value: getAnonymousId() ?? user()?.anonymousId },
      ]

      const { cart } = await createCart({
        lines: [
          {
            quantity: 1,
            merchandiseId: `gid://shopify/ProductVariant/${shopifyGid}`,
          },
        ],
        attributes,
      })

      await handleTrackProductAdded({
        price: cart?.cost?.totalAmount?.amount ?? parseInt(cart?.cost?.totalAmount?.amount ?? 0) ?? 12.49,
        currency: (cart?.cost?.totalAmount?.currencyCode as MoneyCurrencyCode) || 'USD',
        quantity: cart?.totalQuantity || 1,
      })

      const checkoutUrl = mergeUrlParams(cart?.checkoutUrl, { utm_term: 'from-angel-ticket-flow' })

      if (!checkoutUrl) {
        throw new Error('No checkout URL returned from cart creation')
      }

      return push(checkoutUrl)
    } catch (error) {
      handleUnexpectedError({
        context: 'AddPreOrderTicketOption',
        error,
        customToastMessage: t('somethingWentWrongPleaseTryAgainPreOrder', 'Something went wrong, please try again.'),
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    createCart,
    handleUnexpectedError,
    push,
    t,
    user,
    venue.id,
    shopifyGid,
    handleTrackProductAdded,
    query?.promo,
    theatricalRelease?.theatricalSlug,
  ])

  return (
    <li
      className={classNames(
        'relative flex flex-col border p-6 shadow-sm rounded-3xl',
        isDarkMode ? 'bg-core-gray-900' : 'bg-white border-core-gray-200',
      )}
    >
      {/* Venue header */}
      <div className="flex !items-start gap-3">
        {logoUrl && <VenueLogo className="shrink-0" cloudinaryPath={logoUrl} />}
        <FlexColumn className="flex-1">
          <Text
            as={AsH3}
            className="photon-title-sm md:photon-title-md"
            color={isDarkMode ? 'white' : 'black'}
            weight="bold"
          >
            {venue.name}
          </Text>
          <CaptionMD color={isDarkMode ? 'core-gray-400' : 'core-gray-600'}>{venue.addressText}</CaptionMD>
        </FlexColumn>
      </div>

      {/* Presale goal section */}
      {venue.presaleGoal && (
        <FlexColumn className={classNames('mt-4 gap-4 rounded-2xl p-4 bg-core-gray-100 md:flex-row')}>
          <FlexColumn className="gap-2">
            <TitleXS weight={'semibold'}>
              {t('helpBringToThisTheater', 'Help Bring {{movieTitle}} to This Theater', { movieTitle })}
            </TitleXS>
            <CaptionMD weight="medium" color="core-gray-700" className="md:photon-caption-lg">
              {t('joinCommunityToBring', 'Join your community to bring this movie to local theaters.')}
            </CaptionMD>
          </FlexColumn>

          {/* Progress bar */}
          <FlexColumn className="flex-1 gap-1 md:ml-10 md:self-end md:pb-[2px]">
            <FlexRow className="items-center justify-between">
              <CaptionSM weight={'medium'} color={'core-gray-700'}>
                {venue.presaleGoal.ticketsSold} {t('tickets', 'Tickets')}
              </CaptionSM>
              <CaptionSM weight={'medium'} color={'core-gray-700'}>
                {t('communityGoal', 'Community Goal')}: {venue.presaleGoal.nextMilestone}
              </CaptionSM>
            </FlexRow>
            <div
              className={classNames(
                'relative h-2 w-full overflow-hidden rounded-full',
                isDarkMode ? 'bg-core-gray-700' : 'bg-core-gray-200',
              )}
            >
              <div
                className="h-full rounded-full bg-oxide-bright transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <CaptionSM weight={'medium'} color={'core-gray-700'}>
              {Math.round((venue.presaleGoal.ticketsSold / venue.presaleGoal.nextMilestone) * 100)}%{' '}
              {t('ofGoal', 'of Goal')}
            </CaptionSM>
          </FlexColumn>
        </FlexColumn>
      )}
      <FlexColumn className="mt-4 gap-4 md:flex-row">
        <FlexRow className="gap-2">
          <Button onClick={handleCheckout} variant="black" size="sm" disabled={isLoading} className="whitespace-nowrap">
            <TicketIcon size={20} color={'white'} />
            {t('preorderTickets', 'Preorder Tickets')}
          </Button>
        </FlexRow>
        <FlexRow className="!items-start gap-1 md:self-center">
          <CircleInfoIcon size={16} color="oxide-primary" className="shrink-0" />
          <CaptionSM weight={'medium'} color={'core-gray-600'}>
            {t(
              'ticketTransferInfo3',
              'If this theater doesn’t screen the film, you can use your ticket at another nearby location or get a refund.',
            )}
          </CaptionSM>
        </FlexRow>
      </FlexColumn>

      <PresaleVenueIntentModal
        open={showPresaleVenueIntentModal}
        onClose={() => setShowPresaleVenueIntentModal(false)}
        theatricalRelease={theatricalRelease}
        venue={venue}
        handleTrackProductAdded={handleTrackProductAdded}
      />
    </li>
  )
}
